import React from 'react'

import { StrategyType, ReportStatus, PageSpeedDigest } from './common'

import Axios from 'axios'
import { Tabs, Tab } from 'react-bootstrap'
import { ScoreGauge } from './ScoreGauge'
import { StrategyBadge } from './StrategyBadge'
import { DynamicReportTable } from './DynamicReportTable'

const { default: LoadingOverlay } = require('react-loading-overlay')

interface Props {
  url?: string
  strategy?: StrategyType
  digest?: PageSpeedDigest
}

interface State {
  status: ReportStatus
  digest: PageSpeedDigest,
  errorMessage: string,
  activeTab: string,
}

export class DynamicReport extends React.Component<Props, State> {
  static get defaultProps() {
    return {
      columns: {}
    }
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      status: 'processing',
      digest: new PageSpeedDigest(),
      errorMessage: '',
      activeTab: 'asis',
    }
    this.tabSelected = this.tabSelected.bind(this)
  }

  tabSelected(key: string) {
    this.setState({ activeTab: key })
  }

  async loadPageSpeedDigest() {
    if (!this.props.url || !this.props.strategy) throw new Error('url and strategy required')
    try {
      const res = await Axios.get('https://v1.util.simulate.site/pagespeed-insights-digest', {
        params: {
          url: this.props.url,
          strategy: this.props.strategy
        }
      })

      this.setState({ status: 'done', digest: new PageSpeedDigest(res.data) })
    } catch (ex) {
      this.setState({ status: 'error', errorMessage: ex.message })
    }
  }

  componentDidMount() {
    if (this.props.digest) {
      this.setState({ status: 'done', digest: this.props.digest })
    } else {
      this.loadPageSpeedDigest()
    }
  }

  get rank() {
    return Math.min(100, Math.max(1, 101 - this.state.digest.intScore100))
  }

  public renderDigest() {
    return (
      <>
        <div className="mb-4">
          <div className="d-flex">
            {this.state.digest.screenshot && <div className="mr-4">
              <img alt={this.state.digest.finalUrl} className="img-thumbnail" src={this.state.digest.screenshot} style={{ minWidth: '180px' }}></img>
            </div>}
            <div className="mr-4" style={{ minWidth: '220px' }}>
              <ScoreGauge score100={this.state.digest.intScore100}></ScoreGauge>
              <p className="text-center">
                <StrategyBadge className="mr-2" strategy={this.state.digest.strategy}></StrategyBadge>
                {this.state.digest.finalUrl}
              </p>
            </div>
            <div className="flex-glow-1">
              <h2 className="mb-4">100人中 <code>{this.rank}位</code> のページスピード</h2>
              <p>このスコアはわかりやすく言うと「Googleページスピード模試」の結果が</p>
              <p>
                <code>生徒100人の学年において {this.state.digest.score > 0 && <span>1 +</span>} (100 - {this.state.digest.intScore100}) = {this.rank}位</code>
              </p>
              <p>であったことを意味します(100→1位、99→2位…に相当)。</p>
              <p>このスコアはすでに偏差値のような統計値となっており、世界順位と理解して差し支えありません。正確には各種チェックポイント通過タイム(CLSは除く)の対数正規分布モデルにおける累積分布です。</p>
              <p>通常の筆記試験のように減点方式ではないので、100未満でも何か間違いや欠陥があることを意味するものではありません。50以上ならまずは人並み以上の「ページスピード」であると言えます。</p>
              <p>なぜこのスコアになったのか、その内訳と試験対策を見てみましょう。</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  public renderButtonsToPlanTab() {
    return (
      <div className="text-right">
        {this.state.digest.score < 0.5 && <button className="btn btn-primary ml-2" onClick={() => this.tabSelected('plan50')}>プラン50の詳細 →</button>}
        {this.state.digest.score < 0.9 && <button className="btn btn-primary ml-2" onClick={() => this.tabSelected('plan90')}>プラン90の詳細 →</button>}
        {this.state.digest.score < 1.0 && <button className="btn btn-primary ml-2" onClick={() => this.tabSelected('plan100')}>プラン100の詳細 →</button>}
      </div>
    )
  }

  public renderTable() {
    return (
      <div>
        <Tabs defaultActiveKey="asis" activeKey={this.state.activeTab} onSelect={this.tabSelected} id="dynamic-report">
          <Tab eventKey="asis" title="スコアの内訳">
            <p className="alert alert-secondary mt-3">PageSpeed Insightsのスコアは表示速度だけで決まるものではありません。表示速度も含む総合的な起動時間(要素が表示され、負荷が落ち着き快適な操作が可能になる時間)を、次の配点で評価された結果です。まずはどの科目(監査指標)が弱点かを把握しましょう。</p>
            <DynamicReportTable digest={this.state.digest} columns={{ total: true, value: true, score: true, weight: true, allocation: true }}></DynamicReportTable>
            <p className="text-muted"><span role="img">✅</span>スピード指標: <small>全体のスコアは表示速度以外の要因でも上下しますが、このスピード指標の単体スコアが低いページは本当に表示が遅いと言わざるをえません。まず重視すべき科目(監査項目)です。なお、厳密には描画の漸進性を指標化したもので単位は時間(秒)ではないのですが、PageSpeed Insights上での表現に合わせて時間単位で記載します。</small></p>
            <div className="text-right">
              <button className="btn btn-primary" onClick={() => this.tabSelected('comprehension')}>各科目の詳細について →</button>
            </div>
          </Tab>
          <Tab eventKey="comprehension" title="配点の詳細">
            <p className="alert alert-secondary mt-3">それぞれの科目(監査指標)が測定する時間と、その時間に影響を与える代表的な要因は次の通りです。得意科目には配点の上限があること、苦手科目に効果的に対処しなければスコア(総合点)が上がらないことをよく理解してください。</p>
            <DynamicReportTable digest={this.state.digest} columns={{ value: true, allocation: true, description: true, causes: true }}></DynamicReportTable>
            <div className="text-right">
              <button className="btn btn-primary" onClick={() => this.tabSelected('plans')}>スコアの改善プランについて →</button>
            </div>
          </Tab>
          <Tab eventKey="plans" title="スコア改善プランの概要">
            <p className="alert alert-secondary mt-3">スコアを50(人並み=中央値)、90(上位10%グループ入り)、100(1位)とするために、科目毎に短縮すべき時間の目安は次の通りです(達成済みのスコアについてプランは表示されません)。</p>
            <DynamicReportTable digest={this.state.digest} columns={{ total: true, value: true, allocation: true, plan50: this.state.digest.score < 0.5, plan90: this.state.digest.score < 0.9, plan100: this.state.digest.score < 1 }}></DynamicReportTable>
            {this.state.digest.score < 1.0 ? this.renderButtonsToPlanTab() : <p>スコア100につき特に提案はありません。</p>}
          </Tab>
          {this.state.digest.score < 0.5 && <Tab eventKey="plan50" title="プラン50">
            <p className="alert alert-secondary mt-3">まずは人並みレベルのページスピード(50点)以上を目指すためのプランです。</p>
            <DynamicReportTable digest={this.state.digest} columns={{ total: true, description: true, allocation: true, plan50: true, causes: true }}></DynamicReportTable>
          </Tab>}
          {this.state.digest.score < 0.9 && <Tab eventKey="plan90" title="プラン90">
            <p className="alert alert-secondary mt-3">PageSpeed Insightsでは90点以上を高速グループ(緑表示)と既定しています。高すぎる基準に思えますが、確率モデルの特性上、スコア50点から90点までの上昇は比較的短い時間の短縮で達成できます。50点の次は90点を目指してみましょう。</p>
            <DynamicReportTable digest={this.state.digest} columns={{ total: true, description: true, allocation: true, plan90: true, causes: true }}></DynamicReportTable>
          </Tab>}
          {this.state.digest.score < 1.0 && <Tab eventKey="plan100" title="プラン100">
            <p className="alert alert-secondary mt-3">スコアは減点方式ではないので、スコア100にこだわる意味はさほどありません。これ以上速くできないという意味でもありません。</p>
            <DynamicReportTable digest={this.state.digest} columns={{ total: true, description: true, allocation: true, plan100: true, causes: true }}></DynamicReportTable>
          </Tab>}
        </Tabs>
      </div>
    )
  }

  public render() {
    return (
      <LoadingOverlay
        active={this.state.status === 'processing'}
        spinner
        text='Loading your content...'
      >
        {this.state.status === 'done' &&
          <div className="dynamic-report-body">
            {this.renderDigest()}
            {this.renderTable()}
          </div>
        }
      </LoadingOverlay>
    )
  }
}